import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import type {
  CreateQcTemplateRequest,
  QcCheckpointInput,
  QcComparisonOperator,
  QcInspectionType,
  QcTemplateDto,
  QcValueType,
  UpdateQcTemplateRequest,
} from '@/types/quality';
import { criteriaLabel } from '@/utils/qcCheckpoint';

const INSPECTION_TYPES: QcInspectionType[] = ['Visual', 'Measurement', 'Functional', 'Safety', 'Documentation'];
const COMPARISON_OPERATORS: QcComparisonOperator[] = ['EqualTo', 'LessThan', 'GreaterThan', 'LessThanOrEqual', 'GreaterThanOrEqual'];

const emptyLine: QcCheckpointInput = {
  name: '', description: null, inspectionType: 'Visual', valueType: 'YesNo',
  comparisonOperator: null, standardValue: null, toleranceValue: null, isMandatory: false, displayOrder: 0,
};

export default function QcCheckpointMaster() {
  const { t } = useTranslation('qcCheckpointMaster');
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['qc-templates'],
    queryFn: async () => (await apiClient.get<QcTemplateDto[]>('/qc-templates')).data,
  });

  const [open, setOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<QcTemplateDto | null>(null);
  const [viewing, setViewing] = useState<QcTemplateDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<QcTemplateDto | null>(null);

  const [name, setName] = useState('');
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');
  const [isDefault, setIsDefault] = useState(false);
  const [lines, setLines] = useState<QcCheckpointInput[]>([{ ...emptyLine }]);

  const resetForm = () => {
    setName('');
    setStatus('Active');
    setIsDefault(false);
    setLines([{ ...emptyLine }]);
  };

  const openCreate = () => {
    setEditingTemplate(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (template: QcTemplateDto) => {
    setEditingTemplate(template);
    setName(template.name);
    setStatus(template.status as 'Active' | 'Inactive');
    setIsDefault(template.isDefault);
    setLines(
      template.checkpoints.map((c) => ({
        name: c.name, description: c.description, inspectionType: c.inspectionType, valueType: c.valueType,
        comparisonOperator: c.comparisonOperator, standardValue: c.standardValue, toleranceValue: c.toleranceValue,
        isMandatory: c.isMandatory, displayOrder: c.displayOrder,
      })),
    );
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['qc-templates'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.name).map((l, idx) => ({ ...l, displayOrder: idx }));
      if (editingTemplate) {
        const payload: UpdateQcTemplateRequest = { name, status, isDefault, checkpoints: cleanLines };
        return apiClient.put(`/qc-templates/${editingTemplate.id}`, payload);
      }
      const payload: CreateQcTemplateRequest = { name, isDefault, checkpoints: cleanLines };
      return apiClient.post('/qc-templates', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/qc-templates/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<QcTemplateDto>[] = [
    { field: 'name', headerName: t('table.name'), filterable: true },
    { field: 'checkpoints', headerName: t('table.checkpoints'), render: (r) => r.checkpoints.length },
    {
      field: 'isDefault', headerName: t('table.default'),
      render: (r) => (r.isDefault ? <Chip label={t('form.default')} size="small" color="primary" /> : '—'),
    },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.QC_CHECKPOINT_MANAGE}>
            <Button size="small" onClick={() => openEdit(r)}>
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.QC_CHECKPOINT_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('addTemplate')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={templates} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="qc-checkpoint-templates" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? editingTemplate.name : t('addTemplate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label={t('form.name')} value={name} onChange={(e) => setName(e.target.value)} fullWidth />
              {editingTemplate && (
                <TextField select label={t('form.status')} value={status} onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')} fullWidth>
                  <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                  <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                </TextField>
              )}
            </Stack>
            <FormControlLabel
              control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
              label={t('form.default')}
              sx={{ alignSelf: 'flex-start' }}
            />
            {isDefault && (
              <Typography variant="caption" color="text.secondary">{t('form.defaultHelper')}</Typography>
            )}

            <strong>{t('form.checkpoints')}</strong>

            {lines.map((line, idx) => (
              <Stack key={idx} spacing={1} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label={t('form.checkpointName')} value={line.name} fullWidth size="small"
                    onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, name: e.target.value } : l)))}
                  />
                  <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <TextField
                  label={t('form.description')} value={line.description ?? ''} fullWidth size="small"
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, description: e.target.value || null } : l)))}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    select label={t('form.inspectionType')} size="small" sx={{ minWidth: 160 }} value={line.inspectionType}
                    onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, inspectionType: e.target.value as QcInspectionType } : l)))}
                  >
                    {INSPECTION_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>{t(`inspectionTypes.${type}`)}</MenuItem>
                    ))}
                  </TextField>
                  <ToggleButtonGroup
                    size="small" exclusive value={line.valueType}
                    onChange={(_, val: QcValueType | null) => {
                      if (val === null) return;
                      setLines((prev) => prev.map((l, i) => (i === idx
                        ? { ...l, valueType: val, ...(val === 'YesNo' ? { comparisonOperator: null, standardValue: null, toleranceValue: null } : { comparisonOperator: l.comparisonOperator ?? 'EqualTo' }) }
                        : l)));
                    }}
                  >
                    <ToggleButton value="YesNo">{t('valueTypes.YesNo')}</ToggleButton>
                    <ToggleButton value="Value">{t('valueTypes.Value')}</ToggleButton>
                  </ToggleButtonGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={line.isMandatory}
                        onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, isMandatory: e.target.checked } : l)))}
                      />
                    }
                    label={t('form.mandatory')}
                  />
                </Stack>
                {line.valueType === 'Value' && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      select label={t('form.comparisonOperator')} size="small" sx={{ minWidth: 170 }}
                      value={line.comparisonOperator ?? 'EqualTo'}
                      onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, comparisonOperator: e.target.value as QcComparisonOperator } : l)))}
                    >
                      {COMPARISON_OPERATORS.map((op) => (
                        <MenuItem key={op} value={op}>{t(`comparisonOperators.${op}`)}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      type="number" label={t('form.standardValue')} size="small" sx={{ minWidth: 140 }}
                      value={line.standardValue ?? ''}
                      onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, standardValue: e.target.value === '' ? null : Number(e.target.value) } : l)))}
                    />
                    <TextField
                      type="number" label={t('form.toleranceValue')} size="small" sx={{ minWidth: 140 }}
                      value={line.toleranceValue ?? ''} helperText={t('form.toleranceHelper')}
                      onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, toleranceValue: e.target.value === '' ? null : Number(e.target.value) } : l)))}
                    />
                  </Stack>
                )}
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((prev) => [...prev, { ...emptyLine }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('form.addCheckpoint')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('discardChanges')}</Button>
          <Button
            variant="contained"
            disabled={
              !name ||
              lines.some((l) => !l.name || (l.valueType === 'Value' && (!l.comparisonOperator || l.standardValue === null))) ||
              saveMutation.isPending
            }
            onClick={() => saveMutation.mutate()}
          >
            {t('saveTemplate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Same card layout as the QC Inspect popup (QualityControlPage) — name, description,
          inspection type + pass criteria — so what an admin sees here matches exactly what an
          inspector will see when working this template, just without the Pass/Fail controls. */}
      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>{viewing?.name}</span>
            {viewing?.isDefault && <Chip label={t('form.default')} size="small" color="primary" />}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {viewing?.checkpoints.length === 0 && (
              <Typography color="text.secondary">{t('form.noCheckpoints')}</Typography>
            )}
            {viewing?.checkpoints.map((cp) => {
              const criteria = cp.valueType === 'Value' ? criteriaLabel(cp) : null;
              return (
                <Box key={cp.id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle2">{cp.name}</Typography>
                    {cp.isMandatory && <Chip label={t('form.mandatory')} size="small" color="warning" />}
                  </Stack>
                  {cp.description && (
                    <Typography variant="caption" color="text.secondary">{cp.description}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t(`inspectionTypes.${cp.inspectionType}`)}
                    {criteria ? ` · ${t('form.mustBe')} ${criteria}` : ` · ${t('valueTypes.YesNo')}`}
                  </Typography>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewing(null)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteTemplate.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
